// 2026-07-01 - FormuLearning - 관리자: 프로젝트별 사용/운영 통계 (이슈 #32)
// 파일 위치: frontend/src/features/admin/UsageStats.tsx
// 프로젝트별 로드 횟수·총 오퍼레이션 + 선택 시 오퍼레이션(기능버튼)별 클릭 횟수.

import { useEffect, useMemo, useState } from 'react'
import { usageProjects, usageOperations } from '../../api/admin'
import type { UsageProject, UsageOperation } from '../../types/admin'
import { opLabel } from '../../types/admin'

export default function UsageStats() {
  const [projects, setProjects] = useState<UsageProject[]>([])
  const [selected, setSelected] = useState<string | null>(null) // project_id | null(=전체)
  const [ops, setOps] = useState<UsageOperation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    usageProjects().then(setProjects).catch(() => {}).finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    usageOperations(selected ?? undefined).then(setOps).catch(() => setOps([]))
  }, [selected])

  const maxCount = useMemo(() => Math.max(1, ...ops.map((o) => o.count)), [ops])
  const selectedName = selected == null ? 'All projects'
    : projects.find((p) => p.project_id === selected)?.project_name ?? '(unknown)'

  return (
    <div className="grid grid-cols-3 gap-5">
      {/* 프로젝트 요약 */}
      <div className="col-span-1">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Projects</h3>
        {loading ? (
          <p className="text-sm text-text-secondary">Loading…</p>
        ) : (
          <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border-divider)' }}>
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-text-secondary" style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                <tr>
                  <th className="px-3 py-2 font-medium">Project</th>
                  <th className="px-3 py-2 font-medium">Ops</th>
                  <th className="px-3 py-2 font-medium">Loads</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  onClick={() => setSelected(null)}
                  className="cursor-pointer border-t hover:bg-bg-input"
                  style={{ borderColor: 'var(--border-divider)', backgroundColor: selected == null ? 'var(--bg-chip-teal)' : undefined }}
                >
                  <td className="px-3 py-2 text-text-body">All projects</td>
                  <td className="px-3 py-2 text-text-secondary">{projects.reduce((s, p) => s + p.total_operations, 0)}</td>
                  <td className="px-3 py-2 text-text-secondary">{projects.reduce((s, p) => s + p.load_count, 0)}</td>
                </tr>
                {projects.map((p) => (
                  <tr
                    key={p.project_id ?? 'null'}
                    onClick={() => setSelected(p.project_id)}
                    className="cursor-pointer border-t hover:bg-bg-input"
                    style={{ borderColor: 'var(--border-divider)', backgroundColor: selected === p.project_id ? 'var(--bg-chip-teal)' : undefined }}
                  >
                    <td className="px-3 py-2 text-text-body max-w-[160px] truncate" title={p.project_name ?? ''}>{p.project_name ?? '(no project)'}</td>
                    <td className="px-3 py-2 text-text-secondary">{p.total_operations}</td>
                    <td className="px-3 py-2 text-text-secondary">{p.load_count}</td>
                  </tr>
                ))}
                {projects.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-sm text-text-secondary">No operations recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 오퍼레이션별 카운트 */}
      <div className="col-span-2">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
          Operations · <span className="normal-case text-text-body">{selectedName}</span>
        </h3>
        <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border-divider)' }}>
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-text-secondary" style={{ backgroundColor: 'var(--color-bg-panel)' }}>
              <tr>
                <th className="px-3 py-2 font-medium">Operation</th>
                <th className="px-3 py-2 font-medium">Step</th>
                <th className="px-3 py-2 font-medium">Ext. API</th>
                <th className="px-3 py-2 font-medium">Count</th>
              </tr>
            </thead>
            <tbody>
              {ops.map((o) => (
                <tr key={o.operation} className="border-t" style={{ borderColor: 'var(--border-divider)' }}>
                  <td className="px-3 py-2 text-text-body">{opLabel(o.operation)}</td>
                  <td className="px-3 py-2 text-text-secondary">{o.step ?? '—'}</td>
                  <td className="px-3 py-2">
                    {o.triggers_external_api
                      ? <span style={{ color: 'var(--color-accent-teal)' }}>●</span>
                      : <span className="text-text-secondary">—</span>}
                  </td>
                  <td className="px-3 py-2 text-text-body">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2 rounded" style={{ width: `${(o.count / maxCount) * 90 + 4}px`, backgroundColor: 'var(--color-accent-purple)' }} />
                      {o.count}
                    </div>
                  </td>
                </tr>
              ))}
              {ops.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-text-secondary">No operations for this selection.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
